import { useCallback, useEffect, useMemo, useReducer, useState } from "react"

import { useAuth } from "@/auth/AuthProvider"
import {
  useOffersList,
  useOfferVerticals,
  useCreateOffer,
  useUpdateOffer,
  useMyPixels,
  useUsersForFilter,
  useNetworkDomains,
  type Offer,
  type OffersListParams,
  type VerticalOption,
} from "@/api/hooks/offers"
import {
  VERTICAL_PRESETS,
  defaultVerticalDisplayName,
} from "@/lib/verticalCatalog"
import { ApiError } from "@/api/client"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { NavbarTabList, navbarTabClass } from "@/components/NavbarTabs"
import { useNavbarLeftAction, usePageTitle } from "@/layouts/pageChrome"

import {
  ADW_COLUMNS,
  ADW_COLUMN_STORAGE_KEY,
  ADW_VISIBILITY_STORAGE_KEY,
  KLOW_VISIBILITY_STORAGE_KEY,
  ROOTS_VISIBILITY_STORAGE_KEY,
  saveColumnOrder,
  saveColumnVisibility,
} from "./lib/columns"
import type { OffersDatePreset } from "./lib/presets"
import {
  createInitialOffersState,
  offersReducer,
} from "./state/offersReducer"
import { buildTrackingClickUrl } from "@/lib/trackingClickUrl"
import { runPreset } from "./lib/runPreset"
import { OfferFilters } from "./widgets/OfferFilters"
import { OffersTable } from "./widgets/OffersTable"
import { Pagination } from "@/components/Pagination"
import { OfferModal } from "./widgets/OfferModal"
import { KeywordsModal } from "./widgets/KeywordsModal"
import { KlowOfferModal } from "./widgets/KlowOfferModal"
import { KlowOfferEditModal } from "./widgets/KlowOfferEditModal"
import { buildKlowOfferTargetUrl } from "@/lib/klowFeedUrl"

/**
 * React port of `templates/offers/index.html` (T3.5). Parity
 * checklist: `docs/migration/parity/offers.md`.
 *
 * Three network tabs: Roots + ADW + KLOW render offer tables;
 * BRM remains an admin-only placeholder.
 */

export default function OffersPage() {
  usePageTitle("", "Offers")
  const { user, hasNetwork, isAdmin } = useAuth()
  const [state, dispatch] = useReducer(
    offersReducer,
    undefined,
    createInitialOffersState,
  )

  const activeNetwork = useMemo(() => {
    if (state.activeTab === "roots") return "Roots"
    if (state.activeTab === "adw") return "ADW"
    if (state.activeTab === "klow") return "KLOW"
    return null
  }, [state.activeTab])

  const isKlowTab = state.activeTab === "klow"
  const isOffersTableTab =
    state.activeTab === "adw" ||
    state.activeTab === "roots" ||
    state.activeTab === "klow"

  // Data queries
  const queryParams: OffersListParams = useMemo(
    () => ({
      sort_by: state.sortBy,
      sort_order: state.sortOrder,
      page: state.page,
      page_size: state.pageSize,
      network: activeNetwork ?? undefined,
      status: state.status,
      user_ids:
        state.selectedUserIds.length > 0 ? state.selectedUserIds : undefined,
      start_date: state.startDate,
      end_date: state.endDate,
    }),
    [
      state.sortBy,
      state.sortOrder,
      state.page,
      state.pageSize,
      state.status,
      state.selectedUserIds,
      state.startDate,
      state.endDate,
      activeNetwork,
    ],
  )

  const needsVerticalOptions =
    state.offerModalOpen ||
    state.klowOfferModalOpen ||
    state.klowEditOffer != null
  const needsOfferFormSupport = state.offerModalOpen
  const canFilterByUser =
    user?.role === "admin" || user?.role === "team_leader"

  const offersQuery = useOffersList(queryParams, {
    enabled: activeNetwork !== null,
  })
  const offerVerticalsQuery = useOfferVerticals({
    enabled: needsVerticalOptions,
  })
  const pixelsQuery = useMyPixels({
    showAllForAdmin: user?.role === "admin",
    enabled: needsOfferFormSupport,
  })
  const usersQuery = useUsersForFilter({ enabled: canFilterByUser })
  const networkDomainsQuery = useNetworkDomains({
    enabled: needsOfferFormSupport,
  })

  const offerModalOptionsLoading =
    needsOfferFormSupport &&
    (offerVerticalsQuery.isPending ||
      pixelsQuery.isPending ||
      networkDomainsQuery.isPending)
  const klowModalVerticalsLoading =
    (state.klowOfferModalOpen || state.klowEditOffer != null) &&
    offerVerticalsQuery.isPending

  const createMutation = useCreateOffer()
  const updateMutation = useUpdateOffer()

  const offers = useMemo(
    () => offersQuery.data?.data ?? [],
    [offersQuery.data?.data],
  )
  const pagination = offersQuery.data?.pagination
  const users = usersQuery.data?.users ?? []
  const pixels = pixelsQuery.data?.data ?? []
  const networkDomains = networkDomainsQuery.data ?? {}
  /** API (RBAC: team / org) + current page list so verticals stay in sync while paging. */
  const verticalOptions = useMemo((): VerticalOption[] => {
    const byKey = new Map<string, VerticalOption>()
    const add = (o: VerticalOption) => {
      const slug = o.slug.trim()
      const k = slug.toLowerCase()
      if (!k) return
      const name =
        (o.name || "").trim() || defaultVerticalDisplayName(slug)
      byKey.set(k, { slug, name })
    }
    for (const p of VERTICAL_PRESETS) {
      add({ slug: p.slug, name: p.name })
    }
    for (const row of offerVerticalsQuery.data?.data ?? []) {
      const slug = String(row.slug).trim()
      const nm = (row.name || "").trim()
      if (slug) add({ slug, name: nm || defaultVerticalDisplayName(slug) })
    }
    for (const offer of offers) {
      const net = (offer.network || "").toLowerCase()
      if (net !== "roots" && net !== "klow") continue
      const slug = (offer.vertical_slug || "").trim()
      if (!slug) continue
      const name =
        (offer.vertical_name || "").trim() ||
        defaultVerticalDisplayName(slug)
      add({ slug, name })
    }
    return Array.from(byKey.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
  }, [offerVerticalsQuery.data?.data, offers])

  // Toast state (for copy)
  const [toast, setToast] = useState<string | null>(null)
  const showToast = useCallback((msg: string, durationMs = 2000) => {
    setToast(msg)
    setTimeout(() => setToast(null), durationMs)
  }, [])

  // Filter handlers
  const onPresetChange = useCallback(
    (preset: OffersDatePreset) => {
      runPreset(
        preset,
        state.activeTab === "roots" ? "ny" : "local",
        (p, start, end, label) =>
        dispatch({
          type: "SET_PRESET",
          preset: p,
          startDate: start,
          endDate: end,
          displayLabel: label,
        }),
      )
    },
    [state.activeTab],
  )

  const onCustomRangeApply = useCallback(
    (startIso: string, endIso: string, displayLabel: string) => {
      dispatch({
        type: "SET_CUSTOM_RANGE",
        startDate: startIso,
        endDate: endIso,
        displayLabel,
      })
    },
    [],
  )

  const onRefresh = useCallback(() => {
    // Parity: refresh does NOT reset page
    offersQuery.refetch()
  }, [offersQuery])

  // Column reorder
  const onColumnOrderChange = useCallback(
    (order: string[]) => {
      dispatch({ type: "SET_COLUMN_ORDER", order })
      saveColumnOrder(ADW_COLUMN_STORAGE_KEY, order)
    },
    [],
  )

  const currentHiddenColumns = useMemo(() => {
    switch (state.activeTab) {
      case "adw":
        return state.adwHiddenColumns
      case "klow":
        return state.klowHiddenColumns
      default:
        return state.rootsHiddenColumns
    }
  }, [
    state.activeTab,
    state.rootsHiddenColumns,
    state.adwHiddenColumns,
    state.klowHiddenColumns,
  ])

  const handleToggleColumn = useCallback(
    (columnId: string) => {
      dispatch({
        type: "TOGGLE_COLUMN_VISIBILITY",
        tab: state.activeTab,
        columnId,
      })
      const next = new Set(currentHiddenColumns)
      if (next.has(columnId)) next.delete(columnId)
      else next.add(columnId)
      const storageKey =
        state.activeTab === "adw"
          ? ADW_VISIBILITY_STORAGE_KEY
          : state.activeTab === "klow"
            ? KLOW_VISIBILITY_STORAGE_KEY
            : ROOTS_VISIBILITY_STORAGE_KEY
      saveColumnVisibility(storageKey, next)
    },
    [state.activeTab, currentHiddenColumns],
  )

  // Row actions
  const handleCopyLink = useCallback(
    (offer: Offer) => {
      const isKlow = (offer.network || "").toUpperCase() === "KLOW"
      const url = isKlow
        ? buildKlowOfferTargetUrl(offer) || offer.target_url
        : buildTrackingClickUrl(offer.public_slug)
      if (!url) {
        alert("No URL available for this offer")
        return
      }
      navigator.clipboard
        .writeText(url)
        .then(() =>
          showToast(
            isKlow
              ? "RSOC article URL copied"
              : "TikTok destination URL copied",
            2500,
          ),
        )
        .catch(() => alert("Failed to copy URL"))
    },
    [showToast],
  )

  const handleViewLink = useCallback((offer: Offer) => {
    const isKlow = (offer.network || "").toUpperCase() === "KLOW"
    const url = isKlow
      ? buildKlowOfferTargetUrl(offer) || offer.target_url
      : offer.target_url ||
        (offer.partner_domain ? `https://${offer.partner_domain}/` : null)
    if (url) window.open(url, "_blank")
  }, [])

  const handleKeywords = useCallback((offer: Offer) => {
    dispatch({
      type: "OPEN_KEYWORDS_MODAL",
      offerId: offer.id,
      offerName: offer.name,
    })
  }, [])

  const handleDuplicate = useCallback((offer: Offer) => {
    dispatch({
      type: "OPEN_EDIT_MODAL",
      offer: {
        id: null, // null ID = create
        public_slug: null,
        name: `${offer.name} (Copy)`,
        network: offer.network,
        target_url: offer.target_url,
        partner_domain: offer.partner_domain,
        headline: offer.headline,
        terms: offer.terms,
        rac: offer.rac,
        pixel_db_id: offer.pixel_db_id,
        event_name: offer.event_name,
        vertical_slug: offer.vertical_slug,
        vertical_name: offer.vertical_name,
      },
    })
  }, [])

  const handleEdit = useCallback((offer: Offer) => {
    dispatch({
      type: "OPEN_EDIT_MODAL",
      offer: {
        id: offer.id,
        public_slug: offer.public_slug,
        name: offer.name,
        network: offer.network,
        target_url: offer.target_url,
        partner_domain: offer.partner_domain,
        headline: offer.headline,
        terms: offer.terms,
        rac: offer.rac,
        pixel_db_id: offer.pixel_db_id,
        event_name: offer.event_name,
        vertical_slug: offer.vertical_slug,
        vertical_name: offer.vertical_name,
      },
    })
  }, [])

  const handleKlowEdit = useCallback((offer: Offer) => {
    dispatch({ type: "OPEN_KLOW_EDIT_MODAL", offer })
  }, [])

  const handleKlowEditSuccess = useCallback(() => {
    showToast("Offer updated", 2500)
    void offersQuery.refetch()
  }, [offersQuery, showToast])

  const handleArchiveRestore = useCallback(
    async (offer: Offer) => {
      const action =
        offer.status === "Archived" ? "Restore" : "Archive"
      if (!confirm(`${action} this offer?`)) return

      try {
        const newStatus =
          offer.status === "Archived" ? "Active" : "Archived"
        await updateMutation.mutateAsync({
          offerId: offer.id,
          body: { status: newStatus },
        })
        offersQuery.refetch()
      } catch {
        alert(
          `Failed to ${action.toLowerCase()} offer. Please try again.`,
        )
      }
    },
    [updateMutation, offersQuery],
  )

  // Modal submit
  const handleModalSubmit = useCallback(
    async (body: Record<string, unknown>) => {
      try {
        const wasCreate = state.editingOffer?.id == null
        let savedOffer: Offer
        if (state.editingOffer?.id != null) {
          savedOffer = await updateMutation.mutateAsync({
            offerId: state.editingOffer.id,
            body,
          })
        } else {
          savedOffer = await createMutation.mutateAsync(body)
        }
        dispatch({ type: "CLOSE_OFFER_MODAL" })

        if (wasCreate) {
          const dest = buildTrackingClickUrl(savedOffer.public_slug)
          void navigator.clipboard
            .writeText(dest)
            .then(() =>
              showToast(
                "TikTok destination URL copied — paste it as your ad landing page.",
                5500,
              ),
            )
            .catch(() => {
              alert(
                `Offer saved. Use this TikTok destination URL (copy manually):\n\n${dest}`,
              )
            })
        }

        // Auto-switch filter if new offer is Processing and filter is Active
        // Parity: lines 2190-2197
        if (
          savedOffer.status === "Processing" &&
          state.status === "Active"
        ) {
          dispatch({ type: "FORCE_STATUS_FILTER", value: "all" })
        }

        offersQuery.refetch()
      } catch (err) {
        const message =
          err instanceof ApiError
            ? (err.detail &&
                typeof err.detail === "object" &&
                "detail" in err.detail
                ? String((err.detail as { detail: unknown }).detail)
                : err.message)
            : err instanceof Error
              ? err.message
              : "Unknown error"
        alert(`Failed to save offer:\n\n${message}`)
      }
    },
    [
      state.editingOffer,
      state.status,
      createMutation,
      updateMutation,
      offersQuery,
      showToast,
    ],
  )

  const handleKlowOfferSuccess = useCallback(
    (offer: { target_url: string | null; name: string }) => {
      dispatch({ type: "CLOSE_KLOW_MODAL" })
      dispatch({ type: "SET_PAGE", page: 1 })
      void offersQuery.refetch()
      if (offer.target_url) {
        void navigator.clipboard
          .writeText(offer.target_url)
          .then(() =>
            showToast(
              "RSOC article URL copied — paste it as your TikTok landing page.",
              5500,
            ),
          )
          .catch(() => {
            alert(
              `Offer "${offer.name}" saved. Copy this URL manually:\n\n${offer.target_url}`,
            )
          })
      }
    },
    [offersQuery, showToast],
  )

  const errorMessage = useMemo(() => {
    if (offersQuery.isError) {
      const err = offersQuery.error
      return err instanceof Error
        ? err.message
        : "Failed to load offers"
    }
    return null
  }, [offersQuery.isError, offersQuery.error])

  const showRootsTab = hasNetwork("roots")
  const showAdwTab = hasNetwork("adw")
  const showKlowTab = hasNetwork("klow")
  const showBrmTab = isAdmin

  useEffect(() => {
    const allowed =
      (state.activeTab === "roots" && showRootsTab) ||
      (state.activeTab === "adw" && showAdwTab) ||
      (state.activeTab === "klow" && showKlowTab) ||
      (state.activeTab === "brm" && showBrmTab)
    if (allowed) return
    if (showRootsTab) dispatch({ type: "SET_TAB", tab: "roots" })
    else if (showAdwTab) dispatch({ type: "SET_TAB", tab: "adw" })
    else if (showKlowTab) dispatch({ type: "SET_TAB", tab: "klow" })
    else if (showBrmTab) dispatch({ type: "SET_TAB", tab: "brm" })
  }, [state.activeTab, showRootsTab, showAdwTab, showKlowTab, showBrmTab])

  const navbarTabs = useMemo(
    () => (
      <NavbarTabList>
        {showRootsTab && (
          <button
            type="button"
            onClick={() => dispatch({ type: "SET_TAB", tab: "roots" })}
            className={navbarTabClass(state.activeTab === "roots")}
          >
            Roots
          </button>
        )}
        {showAdwTab && (
          <button
            type="button"
            onClick={() => dispatch({ type: "SET_TAB", tab: "adw" })}
            className={navbarTabClass(state.activeTab === "adw")}
          >
            ADW
          </button>
        )}
        {showBrmTab && (
          <button
            type="button"
            onClick={() => dispatch({ type: "SET_TAB", tab: "brm" })}
            className={navbarTabClass(state.activeTab === "brm")}
          >
            BRM
          </button>
        )}
        {showKlowTab && (
          <button
            type="button"
            onClick={() => dispatch({ type: "SET_TAB", tab: "klow" })}
            className={navbarTabClass(state.activeTab === "klow")}
          >
            KLOW
          </button>
        )}
      </NavbarTabList>
    ),
    [
      state.activeTab,
      showRootsTab,
      showAdwTab,
      showBrmTab,
      showKlowTab,
    ],
  )
  useNavbarLeftAction(navbarTabs)

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#3a3a3a]">
      {/* Fixed-screen body: only the table region scrolls */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1.5 pt-1.5 sm:px-2 sm:pt-2">
        {errorMessage && isOffersTableTab && (
          <Alert
            variant="destructive"
            className="mb-2 shrink-0 border-[#663333] bg-[#4a2a2a] text-[#f87171]"
          >
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {showBrmTab && state.activeTab === "brm" ? (
          <div className="flex flex-1 items-center justify-center py-16 text-center text-white">
            <div>
              <p className="text-lg text-[#b0b0b0]">
                {"\uD83D\uDEA7"} Under Construction
              </p>
              <p className="mt-2 text-sm text-[#888]">
                BRM offers will be available soon.
              </p>
            </div>
          </div>
        ) : isOffersTableTab ? (
          <>
            <div className="mb-1.5 shrink-0">
              <OfferFilters
                state={state}
                users={users}
                currentUserRole={user?.role}
                currentUserId={user?.id}
                onStatusChange={(v) =>
                  dispatch({ type: "SET_STATUS", value: v })
                }
                onUserIdsChange={(ids) =>
                  dispatch({ type: "SET_USER_IDS", ids })
                }
                onSearchChange={(v) =>
                  dispatch({ type: "SET_SEARCH", value: v })
                }
                onRefresh={onRefresh}
                onPresetChange={onPresetChange}
                onCustomRangeApply={onCustomRangeApply}
                onCreateOffer={() =>
                  isKlowTab
                    ? dispatch({ type: "OPEN_KLOW_CREATE_MODAL" })
                    : dispatch({ type: "OPEN_CREATE_MODAL" })
                }
                columns={ADW_COLUMNS}
                hiddenColumns={currentHiddenColumns}
                onToggleColumn={handleToggleColumn}
              />
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <OffersTable
                columns={ADW_COLUMNS}
                columnOrder={state.columnOrder}
                hiddenColumns={currentHiddenColumns}
                offers={offers}
                sortBy={state.sortBy}
                sortOrder={state.sortOrder}
                search={state.search}
                isLoading={offersQuery.isFetching && !offersQuery.data}
                isError={offersQuery.isError}
                errorMessage={errorMessage ?? undefined}
                onSort={(key, columnType) =>
                  dispatch({ type: "SET_SORT", key, columnType })
                }
                onColumnOrderChange={onColumnOrderChange}
                onEdit={isKlowTab ? handleKlowEdit : handleEdit}
                onDuplicate={isKlowTab ? undefined : handleDuplicate}
                onArchiveRestore={handleArchiveRestore}
                onCopyLink={handleCopyLink}
                onViewLink={handleViewLink}
                onKeywords={isKlowTab ? undefined : handleKeywords}
              />
            </div>
          </>
        ) : null}
      </div>

      {isOffersTableTab && pagination && (
        <Pagination
          className="border-t border-[#4a4a4a] bg-[#303030] px-2 py-2 sm:px-3"
          page={pagination.page}
          totalPages={pagination.pages}
          pageSize={pagination.page_size}
          total={pagination.total}
          onPageChange={(p) => dispatch({ type: "SET_PAGE", page: p })}
        />
      )}

      <OfferModal
        open={state.offerModalOpen}
        onClose={() => dispatch({ type: "CLOSE_OFFER_MODAL" })}
        editingOffer={state.editingOffer}
        pixels={pixels}
        networkDomains={networkDomains}
        verticalOptions={verticalOptions}
        isLoadingOptions={offerModalOptionsLoading}
        isAdmin={user?.role === "admin"}
        preferredNetwork={
          state.activeTab === "roots"
            ? "Roots"
            : state.activeTab === "adw"
              ? "ADW"
              : undefined
        }
        onSubmit={handleModalSubmit}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      <KeywordsModal
        open={state.keywordsModalOpen}
        onClose={() => dispatch({ type: "CLOSE_KEYWORDS_MODAL" })}
        offerId={state.keywordsOfferId}
        offerName={state.keywordsOfferName}
        startDate={state.startDate}
        endDate={state.endDate}
      />

      <KlowOfferModal
        open={state.klowOfferModalOpen}
        onClose={() => dispatch({ type: "CLOSE_KLOW_MODAL" })}
        verticalOptions={verticalOptions}
        isLoadingVerticals={klowModalVerticalsLoading}
        onSuccess={handleKlowOfferSuccess}
      />

      <KlowOfferEditModal
        open={state.klowEditOffer != null}
        offer={state.klowEditOffer}
        verticalOptions={verticalOptions}
        isLoadingVerticals={klowModalVerticalsLoading}
        onClose={() => dispatch({ type: "CLOSE_KLOW_EDIT_MODAL" })}
        onSuccess={handleKlowEditSuccess}
      />

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 rounded bg-[#333] px-4 py-2 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  )
}
